import React, { useEffect, useState, useContext } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  Settings as SettingsIcon, 
  Users, 
  Shield, 
  UserCheck, 
  UserX,
  Crown,
  UserCog,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Plus,
  Trash2
} from 'lucide-react';
import { authFetch } from '../api';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

export default function AdminSettings() {
  const { t } = useTranslation();
  const { user: currentUser, refreshUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [permissions, setPermissions] = useState({
    readOnly: false,
    pages: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('permissions'); // 'permissions', 'roles', 'custom'
  const [roles, setRoles] = useState([]);
  const [roleHierarchy, setRoleHierarchy] = useState({});
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [customRoleForm, setCustomRoleForm] = useState({
    name: '',
    description: '',
    permissions: {
      readOnly: false,
      pages: {}
    }
  });
  const [showCustomRoleForm, setShowCustomRoleForm] = useState(false);
  const [showRoleManagement, setShowRoleManagement] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('🔍 Loading users from API...');
        
        const response = await authFetch('/users');
        console.log('📊 Raw API response:', response);
        
        const usersArray = response.users || response || [];
        console.log('📊 Users array:', usersArray);
        console.log('📊 Users loaded from API:', usersArray.length);
        
        setUsers(Array.isArray(usersArray) ? usersArray : []);
        
      } catch (err) {
        console.error('❌ Error loading users:', err);
        console.error('❌ Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setError(`Failed to load users: ${err.message}`);
        toast.error('Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    
    // Load users immediately
    fetchUsers();
  }, []);

  // Fetch roles and hierarchy
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        console.log('🔍 Loading roles from API...');
        
        const response = await authFetch('/users/roles');
        const { roles, hierarchy, defaultPermissions } = response;
        
        setRoles(roles || []);
        setRoleHierarchy(hierarchy || {});
        setDefaultPermissions(defaultPermissions || {});
        console.log('📊 Roles loaded from API');
      } catch (err) {
        console.error('❌ Error loading roles:', err);
        toast.error('Failed to load roles');
        
        // Fallback to basic roles if API fails
        setRoles(['Admin', 'Manager', 'Employee', 'Viewer']);
        setRoleHierarchy({ 'Admin': 80, 'Manager': 60, 'Employee': 20, 'Viewer': 10 });
      }
    };
    
    // Load roles immediately
    fetchRoles();
  }, []);

  // Update permissions when selectedUser changes
  useEffect(() => {
    if (selectedUser) {
      handleUserChange(selectedUser);
    }
  }, [selectedUser]);

  // Check if current user has admin access
  const isAdmin = currentUser?.role === 'Admin' || currentUser?.role === 'SuperAdmin' || currentUser?.effectivePermissions?.pages?.settings === true;
  
  // Debug current user state
  console.log('🔍 AdminSettings - Development Mode:', {
    currentUser: currentUser ? {
      name: currentUser.name,
      role: currentUser.role,
      email: currentUser.email,
      effectivePermissions: currentUser.effectivePermissions,
      access: currentUser.access
    } : null,
    isAdmin: isAdmin,
    usersCount: users.length
  });
  
  // Show loading state
  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permissions
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access Admin Settings.</p>
        </div>
      </div>
    );
  }

  // Debug information
  console.log('🔍 AdminSettings Debug:', {
    currentUser: currentUser?.name,
    usersCount: users.length,
    selectedUser,
    loading,
    error
  });

  const pages = [
    { key: 'home', label: 'Home' },
    { key: 'projects', label: 'Projects' },
    { key: 'assignProjects', label: 'Assign Projects' },
    { key: 'dailyLogs', label: 'Daily Logs' },
    { key: 'analytics', label: 'Analytics' },
    { key: 'forecasting', label: 'Forecasting' },
    { key: 'autopark', label: 'AutoPark' },
    { key: 'inventory', label: 'Inventory' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'contracts', label: 'Contracts' },
    { key: 'workers', label: 'Workers' },
    { key: 'users', label: 'Users' },
    { key: 'resources', label: 'Resources' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'settings', label: 'Settings' },
  ];

  // Helper function to check if a user has a valid custom role
  const hasValidCustomRole = (user) => {
    return user?.customRole?.name && user.customRole.name.trim() !== '';
  };

  const handleUserChange = async (userId) => {
    setSelectedUser(userId);
    try {
      console.log('🔍 Loading permissions for user:', userId);
      
      // Find user in the users array
      const user = users.find(u => u._id === userId);
      if (!user) {
        throw new Error('User not found');
      }
      
      console.log('📊 User data found:', user.name);
      
      // Get user access data from API
      const response = await authFetch(`/users/access/${userId}`);
      const access = response.access || {};
      
      setSelectedUserData(user);
      
      const pagesObj = pages.reduce((acc, page) => {
        acc[page.key] = access.pages ? access.pages[page.key] || false : false;
        return acc;
      }, {});
      
      setPermissions({
        readOnly: access.readOnly || false,
        pages: pagesObj
      });
      
    } catch (err) {
      console.error('❌ Error loading user permissions:', err);
      toast.error('Failed to load user permissions');
      setPermissions({
        readOnly: false,
        pages: {}
      });
      setSelectedUserData(null);
    }
  };

  const togglePermission = (key) => {
    setPermissions((prev) => {
      if (key === 'readOnly') {
        return { ...prev, readOnly: !prev.readOnly };
      } else {
        return {
          ...prev,
          pages: {
            ...prev.pages,
            [key]: !prev.pages?.[key]
          }
        };
      }
    });
  };

  const savePermissions = async () => {
    try {
      const completePermissions = {
        readOnly: permissions.readOnly || false,
        pages: {}
      };
      pages.forEach((page) => {
        completePermissions.pages[page.key] = permissions.pages?.[page.key] === true;
      });
      
      console.log('🔍 Saving permissions for user:', selectedUser);
      console.log('📊 Permissions to save:', completePermissions);
      
      await authFetch(`/users/access/${selectedUser}`, {
        method: 'POST',
        body: { access: completePermissions }
      });
      
      console.log('✅ Permissions saved successfully');
      
      // Refresh the permissions to confirm they were saved
      await handleUserChange(selectedUser);
      
      // Refresh the current user's permissions if they were modified
      if (selectedUser === currentUser?._id) {
        await refreshUser();
      }
      
      toast.success('Settings saved and updated.');
      
    } catch (err) {
      console.error('❌ Error saving permissions:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      toast.error('Failed to save settings. Please try again.');
    }
  };

  const assignCustomRole = async () => {
    try {
      console.log('🔍 Assigning custom role:', customRoleForm);
      
      await authFetch(`/users/${selectedUser}/custom-role`, {
        method: 'PUT',
        body: { customRole: customRoleForm }
      });
      
      toast.success('Custom role assigned successfully');
      await handleUserChange(selectedUser);
      
      // Refresh the current user's permissions if they were modified
      if (selectedUser === currentUser?._id) {
        await refreshUser();
      }
      
      setShowCustomRoleForm(false);
      setCustomRoleForm({
        name: '',
        description: '',
        permissions: {
          readOnly: false,
          pages: {}
        }
      });
    } catch (err) {
      console.error('❌ Error assigning custom role:', err);
      toast.error('Failed to assign custom role');
    }
  };

  const removeCustomRole = async () => {
    try {
      await authFetch(`/users/${selectedUser}/custom-role`, {
        method: 'DELETE'
      });
      
      toast.success('Custom role removed successfully');
      
      // Refresh the users list to get updated data
      const response = await authFetch('/users');
      const usersArray = response.users || response || [];
      setUsers(Array.isArray(usersArray) ? usersArray : []);
      
      // Update selected user data
      const updatedUser = usersArray.find(u => u._id === selectedUser);
      if (updatedUser) {
        setSelectedUserData(updatedUser);
      }
      
      // Refresh permissions
      await handleUserChange(selectedUser);
      
      // Refresh the current user's permissions if they were modified
      if (selectedUser === currentUser?._id) {
        await refreshUser();
      }
    } catch (err) {
      console.error('❌ Error removing custom role:', err);
      toast.error('Failed to remove custom role');
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      await authFetch(`/users/${userId}/role`, {
        method: 'PUT',
        body: { role: newRole }
      });
      toast.success(`User role changed to ${newRole}`);
      await handleUserChange(selectedUser);
      
      // Refresh users list
      const res = await authFetch('/users');
      const usersArray = res.users || [];
      setUsers(Array.isArray(usersArray) ? usersArray : []);
      
      // Refresh the current user's permissions if they were modified
      if (userId === currentUser?._id) {
        await refreshUser();
      }
    } catch (err) {
      console.error('❌ Error changing user role:', err);
      toast.error('Failed to change user role');
    }
  };

  const getRoleLevel = (role) => {
    return roleHierarchy[role] || 0;
  };

  const canManageRole = (targetRole) => {
    const currentUserLevel = getRoleLevel(currentUser?.role);
    const targetLevel = getRoleLevel(targetRole);
    return currentUserLevel > targetLevel;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminSettings.advancedSettings')}</h1>
          <p className="text-gray-600 mt-2">{t('adminSettings.manageUserPermissions')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('permissions')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'permissions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
                          <div className="flex items-center gap-2">
                <Shield size={16} />
                {t('adminSettings.permissions')}
              </div>
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roles'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
                          <div className="flex items-center gap-2">
                <Crown size={16} />
                {t('adminSettings.roleManagement')}
              </div>
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'custom'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
                          <div className="flex items-center gap-2">
                <UserCog size={16} />
                {t('adminSettings.customRoles')}
              </div>
          </button>
        </nav>
      </div>
      {/* Main Content Area */}
      <div className="flex flex-col md:flex-row gap-6 items-stretch">
        {/* Sidebar/User List */}
        <div className="w-full md:w-1/3">
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">{t('adminSettings.users')}</h2>
                <RefreshCw 
                  size={16} 
                  className="cursor-pointer text-gray-400 hover:text-gray-600 transition"
                  onClick={() => window.location.reload()}
                />
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {error ? (
                <div className="p-6 text-red-600 text-sm">{error}</div>
              ) : loading ? (
                <div className="p-6 text-gray-500 text-sm">{t('adminSettings.loadingUsers')}</div>
              ) : users.length === 0 ? (
                <div className="p-6 text-gray-500 text-sm">{t('adminSettings.noUsersFound')}</div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <li
                      key={user._id}
                      onClick={() => handleUserChange(user._id)}
                      className={`cursor-pointer px-6 py-4 transition ${
                        selectedUser === user._id
                          ? 'bg-blue-50 border-r-4 border-blue-500'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">{user.fullName || user.name || user.email}</div>
                          <div className="text-sm text-gray-500">
                            {user.role} {hasValidCustomRole(user) && `(${user.customRole.name})`}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {user.isActive ? (
                            <UserCheck size={14} className="text-green-500" />
                          ) : (
                            <UserX size={14} className="text-red-500" />
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            {!selectedUser ? (
              <div className="px-6 py-16 text-center">
                <Users size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('adminSettings.noUserSelected')}</h3>
                <p className="text-gray-500">{t('adminSettings.selectUserToConfigure')}</p>
              </div>
            ) : (
              <>
                {/* User Info Header */}
                {selectedUserData && (
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {selectedUserData.name}
                        </h3>
                        <p className="text-sm text-gray-500">{selectedUserData.email}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {selectedUserData.role}
                          </span>
                          {hasValidCustomRole(selectedUserData) && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {t('adminSettings.custom')}: {selectedUserData.customRole.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedUserData.isActive ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {t('adminSettings.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {t('adminSettings.inactive')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tab Content */}
                {activeTab === 'permissions' && (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">{t('adminSettings.pagePermissions')}</h3>
                      <div className="flex items-center gap-2">
                        {permissions.readOnly ? (
                          <EyeOff size={16} className="text-orange-500" />
                        ) : (
                          <Eye size={16} className="text-green-500" />
                        )}
                        <span className="text-sm text-gray-600">
                          {permissions.readOnly ? t('adminSettings.readOnlyMode') : t('adminSettings.fullAccess')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-blue-100">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('adminSettings.page')}</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('adminSettings.access')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr className="bg-gray-50">
                            <td className="px-6 py-4 font-medium text-gray-900">{t('adminSettings.selectAll')}</td>
                            <td className="px-6 py-4 text-center">
                              <label className="inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                  checked={pages.every((page) => permissions.pages?.[page.key])}
                                  onChange={() => {
                                    const newPages = {};
                                    pages.forEach((page) => {
                                      newPages[page.key] = !pages.every((p) => permissions.pages?.[p.key]);
                                    });
                                    setPermissions((prev) => ({
                                      ...prev,
                                      pages: newPages
                                    }));
                                  }}
                                />
                              </label>
                            </td>
                          </tr>
                          {pages.map((page) => (
                            <tr key={page.key} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{page.label}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <label className="inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                    checked={!!permissions.pages?.[page.key]}
                                    onChange={() => togglePermission(page.key)}
                                  />
                                </label>
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-yellow-50">
                            <td className="px-6 py-4 font-medium text-gray-900">{t('adminSettings.readOnlyMode')}</td>
                            <td className="px-6 py-4 text-center">
                              <label className="inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                  checked={!!permissions.readOnly}
                                  onChange={() => togglePermission('readOnly')}
                                />
                                <span className="ml-2 text-sm text-gray-600">
                                  {t('adminSettings.restrictUserToViewOnly')}
                                </span>
                              </label>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="flex justify-end mt-6">
                      <button
                        onClick={savePermissions}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow transition-colors flex items-center gap-2"
                      >
                        <Save size={16} />
                        {t('adminSettings.saveChanges')}
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'roles' && (
                  <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-6">{t('adminSettings.roleHierarchyAndPermissions')}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Role Hierarchy */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 mb-4">{t('adminSettings.roleHierarchy')}</h4>
                        <div className="space-y-3">
                          {roles.map((role, index) => (
                            <div key={role} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border">
                              <div className="flex items-center gap-2">
                                <Crown size={16} className="text-yellow-500" />
                                <span className="font-medium text-gray-900">{role}</span>
                                <span className="text-xs text-gray-500">{t('adminSettings.level')} {roleHierarchy[role]}</span>
                              </div>
                              {canManageRole(role) ? (
                                <span className="text-xs font-medium text-green-600">{t('adminSettings.canManage')}</span>
                              ) : (
                                <span className="text-xs font-medium text-red-600">{t('adminSettings.cannotManage')}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Default Permissions */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 mb-4">{t('adminSettings.defaultPermissions')}</h4>
                        <div className="space-y-3">
                          {roles.map((role) => (
                            <div key={role} className="p-3 bg-gray-50 rounded-md border">
                              <div className="font-medium text-sm text-gray-900 mb-1">{role}</div>
                              <div className="text-xs text-gray-600">
                                {Object.keys(defaultPermissions[role]?.pages || {}).filter(
                                  key => defaultPermissions[role]?.pages[key]
                                ).length} {t('adminSettings.pagesAccessible')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'custom' && (
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">{t('adminSettings.customRoleManagement')}</h3>
                      {hasValidCustomRole(selectedUserData) ? (
                        <button
                          onClick={removeCustomRole}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
                        >
                          <Trash2 size={16} />
                          {t('adminSettings.removeCustomRole')}
                        </button>
                      ) : (
                        <button
                          onClick={() => setShowCustomRoleForm(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
                        >
                          <Plus size={16} />
                          {t('adminSettings.assignCustomRole')}
                        </button>
                      )}
                    </div>

                    {hasValidCustomRole(selectedUserData) && (
                      <div className="mb-6 p-6 bg-purple-50 border border-purple-200 rounded-lg">
                        <h4 className="font-medium text-purple-800 mb-2">
                          {t('adminSettings.currentCustomRole')}: {selectedUserData.customRole.name}
                        </h4>
                        <p className="text-sm text-purple-600 mb-4">
                          {selectedUserData.customRole.description}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {pages.map((page) => (
                            <div key={page.key} className="flex items-center gap-2">
                              {selectedUserData.customRole.permissions?.pages?.[page.key] ? (
                                <UserCheck size={14} className="text-green-500" />
                              ) : (
                                <UserX size={14} className="text-red-500" />
                              )}
                              <span className="text-xs text-gray-700">{page.label}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-purple-200">
                          <div className="flex items-center gap-2">
                            {selectedUserData.customRole.permissions?.readOnly ? (
                              <EyeOff size={14} className="text-orange-500" />
                            ) : (
                              <Eye size={14} className="text-green-500" />
                            )}
                            <span className="text-sm text-purple-600 font-medium">
                              {selectedUserData.customRole.permissions?.readOnly ? t('adminSettings.readOnlyMode') : t('adminSettings.fullAccess')}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {showCustomRoleForm && (
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="font-medium text-gray-900 mb-6">{t('adminSettings.createCustomRole')}</h4>
                        
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('adminSettings.roleName')}
                            </label>
                            <input
                              type="text"
                              value={customRoleForm.name}
                              onChange={(e) => setCustomRoleForm(prev => ({
                                ...prev,
                                name: e.target.value
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              placeholder={t('adminSettings.enterCustomRoleName')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('adminSettings.description')}
                            </label>
                            <textarea
                              value={customRoleForm.description}
                              onChange={(e) => setCustomRoleForm(prev => ({
                                ...prev,
                                description: e.target.value
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              rows="3"
                              placeholder={t('adminSettings.describeCustomRole')}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                              {t('adminSettings.permissions')}
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {pages.map((page) => (
                                <label key={page.key} className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={customRoleForm.permissions.pages[page.key] || false}
                                    onChange={(e) => setCustomRoleForm(prev => ({
                                      ...prev,
                                      permissions: {
                                        ...prev.permissions,
                                        pages: {
                                          ...prev.permissions.pages,
                                          [page.key]: e.target.checked
                                        }
                                      }
                                    }))}
                                    className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-700">{page.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div className="border-t border-gray-200 pt-4">
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={customRoleForm.permissions.readOnly}
                                onChange={(e) => setCustomRoleForm(prev => ({
                                  ...prev,
                                  permissions: {
                                    ...prev.permissions,
                                    readOnly: e.target.checked
                                  }
                                }))}
                                className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm font-medium text-gray-700">{t('adminSettings.readOnlyMode')}</span>
                            </label>
                          </div>
                        </div>
                        
                        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                          <button
                            onClick={assignCustomRole}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-2 transition-colors"
                          >
                            <Save size={16} />
                            {t('adminSettings.assignCustomRole')}
                          </button>
                          <button
                            onClick={() => setShowCustomRoleForm(false)}
                            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium transition-colors"
                          >
                            {t('adminSettings.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}
